import { FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageContainer } from '../components/layout/PageContainer';

const TERMS_VERSION = '1.0';
const LAST_UPDATED = '8 avril 2026';

export default function Terms() {
  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-16">
        {/* Header */}
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 hover:text-primary mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour à l'accueil
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Conditions Générales d'Utilisation
              </h1>
              <p className="text-sm text-gray-500">
                Version {TERMS_VERSION} · Dernière mise à jour : {LAST_UPDATED}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="prose prose-gray max-w-none text-[15px] leading-relaxed">
          {/* Introduction */}
          <section className="mb-8">
            <p>
              Bienvenue sur Wanzo, la plateforme d'accélération d'entreprises développée par
              i-KiotaHub SAS (ci-après « Wanzo », « nous » ou « la Plateforme »). Wanzo accompagne
              les petites et moyennes entreprises (PME), les institutions financières et les
              organisations de la République Démocratique du Congo dans leur transformation
              numérique, leur structuration financière et leur accès au financement.
            </p>
            <p>
              Les présentes Conditions Générales d'Utilisation (« CGU ») régissent l'ensemble de
              vos interactions avec la Plateforme, y compris l'utilisation des applications Wanzo
              Compta, Wanzo Store, le service d'intelligence artificielle Adha et tout autre
              module ou fonctionnalité proposé par Wanzo.
            </p>
            <p>
              En créant un compte ou en utilisant la Plateforme, vous confirmez avoir lu, compris
              et accepté ces CGU dans leur intégralité. Si vous agissez au nom d'une organisation,
              vous déclarez disposer de l'autorité nécessaire pour engager cette organisation.
            </p>
          </section>

          {/* 1. Objet et mission */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              1. Objet et mission de la Plateforme
            </h2>
            <p>
              Wanzo a pour vocation de structurer les entreprises congolaises afin de les rendre
              éligibles aux financements bancaires et aux opportunités de croissance. La Plateforme
              fournit notamment :
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Des outils de comptabilité et de gestion financière conformes au plan comptable
                OHADA (Wanzo Compta)
              </li>
              <li>
                Un assistant intelligent (Adha) pour l'aide à la saisie comptable, la production
                d'états financiers et le conseil en gestion
              </li>
              <li>
                Un espace de mise en relation entre PME et institutions financières pour faciliter
                l'accès au financement (crédit, leasing, garanties)
              </li>
              <li>
                Une plateforme de commerce en leasing (Wanzo Store) permettant l'acquisition
                d'équipements professionnels
              </li>
              <li>
                Des tableaux de bord analytiques pour le suivi de la performance et la prise de
                décision
              </li>
            </ul>
          </section>

          {/* 2. Inscription et comptes */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              2. Inscription et comptes utilisateur
            </h2>
            <p>Pour utiliser la Plateforme, vous devez :</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Être âgé d'au moins 18 ans et disposer de la capacité juridique requise</li>
              <li>
                Fournir des informations exactes, complètes et à jour lors de l'inscription et
                tout au long de votre utilisation
              </li>
              <li>
                Maintenir la confidentialité de vos identifiants de connexion et ne pas partager
                votre compte avec des tiers
              </li>
              <li>
                Signaler immédiatement toute utilisation non autorisée de votre compte à l'adresse
                support@wanzo.cd
              </li>
            </ul>
            <p>
              Un seul compte principal est autorisé par organisation. L'administrateur du compte
              est responsable de la gestion des accès de ses collaborateurs.
            </p>
          </section>

          {/* 3. Collecte et utilisation des données */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              3. Collecte et utilisation des données
            </h2>
            <p>
              La collecte de vos données est indispensable au bon fonctionnement de la Plateforme.
              En acceptant ces CGU, vous consentez à la collecte et au traitement des données
              suivantes :
            </p>
            <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
              3.1 Données collectées
            </h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <strong>Données d'identification</strong> : nom, prénom, adresse e-mail, numéro de
                téléphone, pièce d'identité du représentant légal
              </li>
              <li>
                <strong>Données d'entreprise</strong> : dénomination sociale, RCCM, NIF, ID
                National, secteur d'activité, adresse du siège, données des dirigeants
              </li>
              <li>
                <strong>Données financières</strong> : écritures comptables, états financiers,
                données de facturation, bilans, comptes de résultat
              </li>
              <li>
                <strong>Données d'usage</strong> : historique de connexion, actions effectuées
                sur la Plateforme, interactions avec l'assistant Adha
              </li>
            </ul>

            <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
              3.2 Finalités du traitement
            </h3>
            <p>Vos données sont traitées exclusivement pour :</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Vous fournir les services de la Plateforme (comptabilité, gestion, reporting)
              </li>
              <li>
                Structurer votre dossier d'entreprise en vue de le rendre éligible au financement
                bancaire, si vous en faites la demande
              </li>
              <li>Améliorer la qualité et la pertinence des services proposés</li>
              <li>
                Assurer la conformité réglementaire et la sécurité de la Plateforme
              </li>
              <li>Vous adresser des communications relatives à votre compte et aux services</li>
            </ul>

            <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
              3.3 Partage des données avec des tiers
            </h3>
            <p>
              Wanzo ne vend jamais vos données à des tiers. Le partage de vos données
              d'entreprise avec des institutions financières partenaires ne se fait que dans les
              cas suivants :
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Vous en avez fait la demande explicite (par exemple en soumettant un dossier de
                demande de financement)
              </li>
              <li>
                Le partage est nécessaire pour vous fournir un service que vous avez sollicité
                (leasing, crédit, garantie)
              </li>
              <li>
                Une obligation légale nous y contraint (réquisition judiciaire, contrôle fiscal)
              </li>
            </ul>
            <p>
              Dans tous les cas, le partage vise exclusivement à servir les intérêts de votre
              entreprise et à faciliter votre accès aux financements.
            </p>
          </section>

          {/* 4. Consentement et autorisation */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              4. Consentement et autorisation d'utilisation des données
            </h2>
            <p>
              En acceptant ces CGU, vous autorisez Wanzo à utiliser les données de votre
              entreprise telles que décrites à l'article 3. Cette autorisation comprend :
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                L'analyse automatisée de vos données comptables et financières par notre assistant
                intelligent (Adha) afin de vous fournir des recommandations personnalisées
              </li>
              <li>
                La génération d'états financiers, de rapports d'analyse et d'indicateurs de
                performance à partir de vos données
              </li>
              <li>
                La constitution de votre dossier de financement, incluant les données comptables
                et les performances de votre entreprise, lorsque vous sollicitez une mise en
                relation avec une institution financière
              </li>
            </ul>
            <p>
              Vous conservez à tout moment le droit de retirer votre consentement pour le partage
              de vos données avec les institutions financières partenaires, en nous contactant à
              l'adresse support@wanzo.cd. Ce retrait n'affecte pas le fonctionnement des modules
              de gestion et de comptabilité.
            </p>
          </section>

          {/* 5. Obligations des utilisateurs */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              5. Obligations des utilisateurs
            </h2>
            <p>En utilisant la Plateforme, vous vous engagez à :</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Fournir des informations véridiques et à jour sur votre identité et votre
                entreprise
              </li>
              <li>
                Respecter les lois et réglementations applicables en République Démocratique du
                Congo et en matière de droit des affaires OHADA
              </li>
              <li>
                Ne pas utiliser la Plateforme à des fins frauduleuses, illégales ou portant
                atteinte aux droits de tiers
              </li>
              <li>
                Ne pas tenter de contourner les mesures de sécurité de la Plateforme ni
                d'accéder à des données d'autres utilisateurs
              </li>
              <li>
                Maintenir la confidentialité des informations auxquelles vous avez accès via la
                Plateforme
              </li>
              <li>
                Signaler toute anomalie, erreur ou utilisation abusive à l'équipe Wanzo
              </li>
            </ul>
          </section>

          {/* 6. Services financiers */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              6. Dispositions spécifiques aux institutions financières
            </h2>
            <p>
              Les institutions financières utilisant la Plateforme s'engagent en outre à :
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Respecter les réglementations de la Banque Centrale du Congo (BCC) applicables à
                leur activité
              </li>
              <li>
                Disposer de licences et autorisations valides pour exercer leur activité
              </li>
              <li>
                Se conformer aux normes en matière de lutte contre le blanchiment de capitaux et
                le financement du terrorisme (LAB/FT)
              </li>
              <li>
                Assurer une protection renforcée des données financières des PME partagées dans le
                cadre des demandes de financement
              </li>
              <li>
                Traiter les dossiers de financement avec diligence et dans les délais convenus
              </li>
            </ul>
          </section>

          {/* 7. Tarification */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              7. Tarification et paiements
            </h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Les tarifs des abonnements et services sont affichés en dollars américains (USD)
                et peuvent être réglés via Mobile Money ou d'autres moyens de paiement proposés
              </li>
              <li>
                Les abonnements sont renouvelés automatiquement à l'échéance, sauf résiliation
                dans les conditions prévues
              </li>
              <li>
                Les tokens premium achetés sont valables sans expiration et ne sont pas
                remboursables
              </li>
              <li>
                Wanzo se réserve le droit de modifier ses tarifs avec un préavis de trente (30)
                jours, notifié par e-mail ou via la Plateforme
              </li>
              <li>
                En cas de non-paiement, l'accès aux fonctionnalités payantes peut être suspendu
                après une période de grâce de trois (3) jours
              </li>
            </ul>
          </section>

          {/* 8. Propriété intellectuelle */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              8. Propriété intellectuelle
            </h2>
            <p>
              L'ensemble des éléments composant la Plateforme (code source, interfaces, logos,
              textes, algorithmes, modèles d'intelligence artificielle) sont la propriété exclusive
              d'i-KiotaHub SAS et sont protégés par les lois relatives à la propriété
              intellectuelle.
            </p>
            <p>
              L'utilisation de la Plateforme ne vous confère aucun droit de propriété sur ces
              éléments. Toute reproduction, modification ou distribution non autorisée est
              strictement interdite.
            </p>
            <p>
              Les données que vous saisissez sur la Plateforme restent votre propriété. Vous
              accordez à Wanzo une licence d'utilisation limitée aux finalités décrites à
              l'article 3.
            </p>
          </section>

          {/* 9. Sécurité et confidentialité */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              9. Sécurité et confidentialité
            </h2>
            <p>
              Wanzo met en place les mesures techniques et organisationnelles nécessaires pour
              protéger vos données :
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Chiffrement des données en transit et au repos</li>
              <li>
                Authentification sécurisée via Auth0 avec prise en charge de l'authentification à
                deux facteurs
              </li>
              <li>
                Hébergement des données sur des infrastructures conformes aux standards de
                l'industrie
              </li>
              <li>Audits de sécurité réguliers et surveillance continue des systèmes</li>
              <li>
                Contrôle d'accès basé sur les rôles, limitant la visibilité des données au strict
                nécessaire
              </li>
            </ul>
          </section>

          {/* 10. Limitation de responsabilité */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              10. Limitation de responsabilité
            </h2>
            <p>
              Wanzo agit en qualité de prestataire technologique et d'intermédiaire. À ce titre :
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Nous ne sommes pas responsables des décisions commerciales ou financières que vous
                prenez sur la base des informations ou analyses fournies par la Plateforme
              </li>
              <li>
                Nous ne garantissons pas l'obtention d'un financement auprès des institutions
                financières partenaires
              </li>
              <li>
                Les recommandations de l'assistant Adha sont fournies à titre indicatif et ne
                constituent pas un conseil financier, juridique ou fiscal
              </li>
              <li>
                Nous ne sommes pas responsables des interruptions de service liées à des
                circonstances indépendantes de notre volonté (coupures d'électricité, pannes
                réseau, force majeure)
              </li>
            </ul>
          </section>

          {/* 11. Résiliation */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              11. Résiliation
            </h2>
            <p>
              Vous pouvez résilier votre compte à tout moment en adressant une demande à
              support@wanzo.cd. En cas de résiliation :
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Vos données comptables et financières resteront accessibles en lecture seule
                pendant une période de trente (30) jours suivant la résiliation
              </li>
              <li>
                Vous pourrez exporter vos données avant la suppression définitive de votre compte
              </li>
              <li>
                Les tokens premium non consommés ne donnent pas lieu à remboursement
              </li>
            </ul>
            <p>
              Wanzo se réserve le droit de suspendre ou de résilier un compte en cas de violation
              des présentes CGU, de comportement frauduleux ou de non-paiement persistant.
            </p>
          </section>

          {/* 12. Droit applicable */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              12. Droit applicable et règlement des litiges
            </h2>
            <p>
              Les présentes CGU sont régies par le droit en vigueur en République Démocratique du
              Congo, complété le cas échéant par les dispositions du droit OHADA.
            </p>
            <p>
              En cas de litige, les parties s'engagent à rechercher une solution amiable dans un
              délai de trente (30) jours. À défaut d'accord, le litige sera soumis aux tribunaux
              compétents de la ville de Goma, République Démocratique du Congo.
            </p>
          </section>

          {/* 13. Modifications */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              13. Modifications des présentes conditions
            </h2>
            <p>
              Wanzo se réserve le droit de modifier ces CGU à tout moment. Les modifications
              significatives seront notifiées par e-mail et par un avis visible sur la Plateforme
              au moins quinze (15) jours avant leur entrée en vigueur.
            </p>
            <p>
              La poursuite de votre utilisation de la Plateforme après l'entrée en vigueur des
              modifications vaut acceptation des nouvelles conditions. Si vous n'acceptez pas les
              modifications, vous pouvez résilier votre compte conformément à l'article 11.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 border border-gray-200 rounded-xl p-6 mt-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Contact</h2>
            <p className="text-gray-700">
              Pour toute question relative aux présentes Conditions Générales d'Utilisation ou à
              la protection de vos données, vous pouvez nous contacter :
            </p>
            <ul className="mt-3 space-y-1 text-gray-700">
              <li>
                <strong>E-mail</strong> : support@wanzo.cd
              </li>
              <li>
                <strong>Téléphone</strong> : +243 979 588 462
              </li>
              <li>
                <strong>Adresse</strong> : RDC, Nord-Kivu, Goma, Commune de Goma, Quartier Lac
                Vert, Avenue Kabanda
              </li>
              <li>
                <strong>Éditeur</strong> : i-KiotaHub SAS · RCCM CD/GOMA/RCCM/23-B-00196 · NIF
                A2321658S
              </li>
            </ul>
          </section>
        </article>
      </div>
    </PageContainer>
  );
}

export { TERMS_VERSION };
